import { SimpleReadOnlyEvent } from '@passionware/simple-event';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { throttle } from 'lodash';
import { V1Action } from 'v4/migration/actionAsEventMiddleware';
import { ReconciliationApi } from '../../../api/reconciliation/reconciliation.api';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import { WithServices } from '../../../platform/ts/services';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { ReconciliationService } from './ReconciliationService';
import { maybe } from '@passionware/monads';

export function createReconciliationService(
  config: WithServices<[WithErrorService, WithMessageService]> & {
    api: ReconciliationApi;
    queryClient: QueryClient;
    v1Event: SimpleReadOnlyEvent<V1Action>;
  }
): ReconciliationService {
  const {
    api,
    queryClient,
    v1Event,
    services: { errorService, messageService }
  } = config;
  const interceptor = errorService.withCategory('ReconciliationService');

  // Subscribe to system effects to handle cache invalidation on mutations
  messageService.reportSystemEffect.subscribeToRequest(async message => {
    for (const effect of message.request.scope) {
      switch (effect.entity) {
        case 'booking':
        case 'expense':
        case 'production': {
          // Invalidate all reconciliation list queries when related entities change
          queryClient.removeQueries({
            queryKey: ['reconciliations-v5', 'list'],
            type: 'inactive'
          });
          await queryClient.invalidateQueries({
            queryKey: ['reconciliations-v5', 'list'],
            type: 'active'
          });
        }
      }
    }
    message.sendResponse();
  });

  const invalidateAll = throttle(
    async () => {
      queryClient.removeQueries({
        queryKey: ['reconciliations-v5'],
        type: 'inactive'
      });
      await queryClient.invalidateQueries({
        queryKey: ['reconciliations-v5'],
        type: 'active'
      });
    },
    3000,
    {
      leading: true,
      trailing: true
    }
  );

  // Listen to v1 Redux actions and sync to TanStack Query cache
  v1Event.addListener(async event => {
    switch (event.type) {
      case 'CREATE_EXPENSE_RECEIPT_SUCCESS':
      case 'UPDATE_EXPENSE_RECEIPT_SUCCESS':
      case 'DELETE_EXPENSE_RECEIPT_SUCCESS':
      case 'CREATE_INVOICES_SUCCESS':
      case 'UPDATE_INVOICE_SUCCESS':
      case 'DELETE_INVOICE_SUCCESS':
      case 'BOOKING_ARCHIVED':
      case 'BOOKING_CANCELLED':
      case 'BOOKING_CREATED':
      case 'BOOKING_UPDATED':
      case 'PRODUCTION_ARCHIVED':
      case 'PRODUCTION_CREATED':
      case 'PRODUCTION_UPDATED':
        await invalidateAll();
        return;
    }
  });

  return {
    useList: query =>
      ensureIdleQuery(
        query,
        useQuery({
          queryKey: ['reconciliations-v5', 'list', query],
          enabled: maybe.isPresent(query),
          queryFn: () => interceptor.wrap(() => api.getList(query))(),
          staleTime: 30000 // 30 seconds
        })
      )
  };
}
